import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, User, Ticket, Loader2, Building2, Check, X } from "lucide-react";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

function scorePassword(pw) {
  let s = 0;
  if (pw.length >= 12) s++;
  if (pw.length >= 16) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

const LABELS = ["Molto debole", "Debole", "Discreta", "Buona", "Robusta"];
const COLORS = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-lime-500", "bg-green-600"];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);

  const score = useMemo(() => scorePassword(password), [password]);
  const longEnough = password.length >= 12;

  const submit = async (e) => {
    e.preventDefault();
    if (!longEnough) {
      toast.error("La password deve avere almeno 12 caratteri");
      return;
    }
    setLoading(true);
    try {
      await register({
        displayName: displayName.trim(),
        email: email.trim().toLowerCase(),
        password,
        inviteCode: inviteCode.trim() || null,
      });
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registrazione non riuscita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-[100dvh] overflow-y-auto w-full flex flex-col bg-background">
      <div className="flex justify-between items-center p-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-sky-500 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <span className="text-lg font-bold">Relatio</span>
        </div>
        <ThemeToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-bold tracking-tight">Crea il tuo account</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-8">
            Serve un codice invito valido, salvo per il primo amministratore.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Nome visualizzato</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="name" required data-testid="register-name-input" className="pl-10 h-11"
                  placeholder="Mario Rossi" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="email" type="email" required data-testid="register-email-input" className="pl-10 h-11"
                  placeholder="nome@azienda.it" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="password" type="password" required data-testid="register-password-input" className="pl-10 h-11"
                  placeholder="Almeno 12 caratteri" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              {password && (
                <div className="space-y-1.5 pt-1" data-testid="password-strength">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < score ? COLORS[score] : "bg-muted"}`} />
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{LABELS[score]}</span>
                    <span className={`flex items-center gap-1 ${longEnough ? "text-green-600" : "text-muted-foreground"}`}>
                      {longEnough ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} min. 12 caratteri
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite">Codice invito</Label>
              <div className="relative">
                <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="invite" data-testid="register-invite-input" className="pl-10 h-11 font-mono"
                  placeholder="Lascia vuoto se sei il primo utente" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit-button"
              className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrati"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Hai già un account?{" "}
            <Link to="/login" data-testid="go-login-link" className="text-sky-600 font-semibold hover:underline">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
